import { createTheme } from '@mantine/core'
import type { MantineColorsTuple, MantineThemeOverride } from '@mantine/core'

/**
 * One canonical theme, in light and dark form.
 *
 * Every colour resolves through the CSS variables in `tokens.css`, so the two
 * variants are near-identical objects — they differ only in `primaryShade`,
 * which picks the mode-appropriate step out of the single `sea` tuple below.
 * That is deliberate: it keeps Mantine's own palette in sync with the tokens
 * without maintaining two colour scales.
 */

/** Sea blue. Index 6 is the light-mode accent, index 5 the dark-mode one. */
const sea: MantineColorsTuple = [
  '#EAF2FB',
  '#D0E2F5',
  '#A3C7EA',
  '#74A9DE',
  '#5093D5',
  '#3B85D0', // dark-mode accent   (--color-accent-primary, dark)
  '#2C6FA8', // light-mode accent  (--color-accent-primary, light)
  '#245C8C',
  '#1C4A70',
  '#143654',
]

const fontConfig = {
  // Quoting a var() is valid here: Mantine emits these into CSS custom
  // properties, so the indirection resolves normally and tokens.css stays the
  // single source of truth for the family stacks.
  fontFamily: 'var(--font-ui)',
  fontFamilyMonospace: 'var(--font-mono)',
  headings: {
    fontFamily: 'var(--font-display)',
    fontWeight: '600',
  },
}

/**
 * Shared component overrides. All colours come from tokens.css, so these are
 * identical across modes.
 *
 * IMPORTANT: Mantine `styles` objects render as INLINE styles and cannot target
 * attribute selectors. Anything stateful — NavLink `[data-active]`, tab
 * selection, stage steps — must be styled in CSS against the tokens instead.
 */
const sharedComponents = {
  AppShell: {
    styles: {
      main: { backgroundColor: 'var(--color-bg-main)' },
      header: {
        backgroundColor: 'var(--color-bg-surface)',
        borderBottomColor: 'var(--color-border)',
      },
      // The rail is NOT a surface — it stays deep navy in both modes.
      navbar: {
        backgroundColor: 'var(--rail-bg)',
        borderRightColor: 'var(--rail-border)',
        color: 'var(--rail-fg)',
      },
    },
  },
  Card: {
    styles: {
      root: {
        backgroundColor: 'var(--color-bg-card)',
        borderColor: 'var(--color-border)',
      },
    },
  },
  Title: {
    styles: {
      root: { color: 'var(--color-text-primary)' },
    },
  },
  Table: {
    styles: {
      table: { borderColor: 'var(--color-border)' },
      th: {
        backgroundColor: 'var(--color-table-header-bg)',
        color: 'var(--color-text-primary)',
      },
    },
  },
  Badge: { defaultProps: { color: 'sea' } },
  Button: { defaultProps: { color: 'sea' } },
}

export const lightTheme: MantineThemeOverride = createTheme({
  ...fontConfig,
  primaryColor: 'sea',
  primaryShade: 6,
  colors: { sea },
  other: { themeName: 'Light' },
  components: sharedComponents,
})

export const darkTheme: MantineThemeOverride = createTheme({
  ...fontConfig,
  primaryColor: 'sea',
  primaryShade: 5,
  colors: { sea },
  other: { themeName: 'Dark' },
  components: sharedComponents,
})
